// src/pages/task/TaskTemplateEditPage.tsx
// Task template edit — mirrors the create form, prefilled from GetTemplate.
// UpdateTemplateRequest has no field_mask (full-object update), so the save
// merges the original template with the edited fields before sending.

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useTaskTemplate, useI18n } from '@react/hooks';
import { Button, ErrorAlert } from '@react/components';
import { TitleInput, AutoGrowTextarea, FormRow, SegmentedControl } from '@/components/form';
import { Select } from '@/components/Select';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { TagPicker } from '@/components/TagPicker';
import { TemplateChecklistsEditor } from '@/features/task/TemplateChecklistsEditor';
import { taskTypeOptions } from '@/features/task/logic';
import { SCOPE_ENTITY_TYPES, durationToSeconds, makeEntity, secondsToDuration } from '@/features/task/panelHelpers';
import { useScope } from '@/hooks/useScope';
import type { Tag } from '@core/types/tag';
import type { TaskChecklist } from '@core/types/task';

const PRIORITY_OPTIONS = [
  { value: '', labelKey: 'task.template.priorityNone' },
  { value: 'TASK_PRIORITY_LOWEST', labelKey: 'task.priority.lowest' },
  { value: 'TASK_PRIORITY_LOW', labelKey: 'task.priority.low' },
  { value: 'TASK_PRIORITY_MEDIUM', labelKey: 'task.priority.medium' },
  { value: 'TASK_PRIORITY_HIGH', labelKey: 'task.priority.high' },
  { value: 'TASK_PRIORITY_HIGHEST', labelKey: 'task.priority.highest' },
];

/**
 * Task template edit page.
 */
export const TaskTemplateEditPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useTaskTemplate();
  const { t } = useI18n();
  const scope = useScope();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [defaultTitle, setDefaultTitle] = useState('');
  const [defaultDescription, setDefaultDescription] = useState('');
  const [defaultType, setDefaultType] = useState('');
  const [defaultPriority, setDefaultPriority] = useState('');
  const [defaultTags, setDefaultTags] = useState<Tag[]>([]);
  const [defaultChecklists, setDefaultChecklists] = useState<TaskChecklist[]>([]);
  const [estimatedHours, setEstimatedHours] = useState('');
  const [scopeType, setScopeType] = useState<string>(SCOPE_ENTITY_TYPES[0]);
  const [scopeId, setScopeId] = useState('');
  const [autoCreate, setAutoCreate] = useState(false);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (id) store.GetTemplate(id);
    return () => store.clearCurrent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const item = store.currentItem;
    if (!item) return;
    setName(item.name || '');
    setDescription(item.description || '');
    setDefaultTitle(item.default_title || '');
    setDefaultDescription(item.default_description || '');
    setDefaultType((item.default_type as string) || '');
    setDefaultPriority((item.default_priority as string) || '');
    setDefaultTags((item.default_tags || []).map((name) => ({ name })) as unknown as Tag[]);
    setDefaultChecklists(item.default_checklists?.checklists || []);
    const seconds = durationToSeconds(item.default_estimated_duration);
    setEstimatedHours(seconds > 0 ? String(seconds / 3600) : '');
    setScopeType(item.scope_entity_type || SCOPE_ENTITY_TYPES[0]);
    setScopeId(item.scope_entity?.id || '');
    setAutoCreate(item.auto_create ?? false);
    setIsActive(item.is_active ?? true);
  }, [store.currentItem]);

  const handleSubmit = async () => {
    if (!id || !store.currentItem) return;
    setIsSubmitting(true);
    try {
      const hours = parseFloat(estimatedHours);
      await store.UpdateTemplate(id, {
        template: {
          ...store.currentItem,
          name: name.trim(),
          description: description.trim(),
          default_title: defaultTitle.trim(),
          default_description: defaultDescription.trim(),
          default_type: defaultType || undefined,
          default_priority: defaultPriority || undefined,
          default_tags: defaultTags.map((tag) => tag.name).filter(Boolean),
          default_checklists: defaultChecklists.length > 0 ? { checklists: defaultChecklists } : undefined,
          default_estimated_duration: hours > 0 ? secondsToDuration(hours * 3600) : undefined,
          scope_entity: scopeId.trim() ? makeEntity(scopeId.trim(), scopeType, scope) : undefined,
          scope_entity_type: scopeId.trim() ? scopeType : undefined,
          auto_create: autoCreate,
          is_active: isActive,
        },
      } as never);
      navigate(`/task-templates/${id}`);
    } catch {
      // Error is handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = !!name.trim();

  if (store.isLoadingItem) {
    return (
      <div className="mx-auto max-w-xl space-y-4">
        <div className="animate-pulse">
          <div className="mb-4 h-3 w-48 rounded bg-muted" />
          <div className="h-8 w-56 rounded bg-muted" />
        </div>
        <div className="space-y-4 rounded-2xl border border-border p-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="mb-2 h-3 w-20 rounded bg-muted" />
              <div className="h-9 w-full rounded bg-muted" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!store.isLoadingItem && !store.currentItem && store.error) {
    return (
      <div className="mx-auto max-w-xl space-y-4">
        <ErrorAlert error={store.error} />
        <Link to="/task-templates" className="inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl space-y-4">
      <nav className="text-sm">
        <Link to="/tasks" className="text-muted-foreground hover:text-foreground">
          {t('task.list')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/task-templates" className="text-muted-foreground hover:text-foreground">
          {t('task.templates')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{t('common.edit')}</span>
      </nav>

      {!!store.error && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      <div className="rounded-2xl border border-border bg-popover shadow-sm">
        <div className="px-4 pb-1 pt-4">
          <TitleInput
            value={name}
            onChange={setName}
            placeholder={t('task.template.namePlaceholder')}
            aria-label={t('task.template.name')}
          />
          <AutoGrowTextarea
            value={description}
            onChange={setDescription}
            placeholder={t('task.template.descriptionPlaceholder')}
            className="mt-1"
          />
        </div>

        <div className="space-y-3 border-t border-border px-4 py-3">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t('task.template.defaults')}
          </p>
          <FormRow label={t('task.template.defaultTitle')} labelWidth="sm" htmlFor="tpl-default-title">
            <input
              id="tpl-default-title"
              type="text"
              value={defaultTitle}
              onChange={(e) => setDefaultTitle(e.target.value)}
              placeholder={t('task.field.titlePlaceholder')}
              className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </FormRow>
          <FormRow label={t('task.template.defaultDescription')} labelWidth="sm" htmlFor="tpl-default-desc">
            <AutoGrowTextarea
              id="tpl-default-desc"
              value={defaultDescription}
              onChange={setDefaultDescription}
              placeholder={t('task.field.descriptionPlaceholder')}
              className="rounded-md border border-input bg-background px-3 py-1.5"
            />
          </FormRow>
          <FormRow label={t('task.field.type')} labelWidth="sm">
            <Select
              value={defaultType}
              onChange={(e) => setDefaultType(e.target.value)}
              options={[{ value: '', label: t('task.template.typeNone') }, ...taskTypeOptions.map((o) => ({ value: o.value, label: t(o.labelKey) }))]}
              fullWidth={false}
              className="w-52"
              aria-label={t('task.field.type')}
            />
          </FormRow>
          <FormRow label={t('task.field.priority')} labelWidth="sm">
            <Select
              value={defaultPriority}
              onChange={(e) => setDefaultPriority(e.target.value)}
              options={PRIORITY_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
              fullWidth={false}
              className="w-52"
              aria-label={t('task.field.priority')}
            />
          </FormRow>
          <FormRow label={t('task.field.tags')} labelWidth="sm">
            <TagPicker value={defaultTags} onChange={setDefaultTags} placeholder={t('tag.selectTags')} />
          </FormRow>
          <FormRow label={t('task.estimatedHours')} labelWidth="sm" htmlFor="tpl-estimated-hours">
            <input
              id="tpl-estimated-hours"
              type="text"
              inputMode="decimal"
              value={estimatedHours}
              onChange={(e) => setEstimatedHours(e.target.value.replace(/[^0-9.]/g, ''))}
              placeholder="8"
              className="w-24 rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </FormRow>
        </div>

        <div className="space-y-2 border-t border-border px-4 py-3">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t('task.defaultChecklists')}
          </p>
          <TemplateChecklistsEditor value={defaultChecklists} onChange={setDefaultChecklists} />
        </div>

        <div className="space-y-3 border-t border-border px-4 py-3">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t('task.scope')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FormRow label={t('task.policies.scopeType')} labelWidth="sm">
              <Select value={scopeType} onChange={(e) => setScopeType(e.target.value)} fullWidth={false} className="w-full">
                {SCOPE_ENTITY_TYPES.map((st) => <option key={st} value={st}>{st}</option>)}
              </Select>
            </FormRow>
            <FormRow label={t('task.policies.scopeId')} labelWidth="sm">
              <input
                type="text"
                value={scopeId}
                onChange={(e) => setScopeId(e.target.value)}
                placeholder={t('task.policies.scopeIdPlaceholder')}
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>
          </div>
          <p className="text-xs text-muted-foreground">{t('task.policies.scopeIdHint')}</p>
        </div>

        <div className="space-y-0.5 border-t border-border px-4 py-3">
          <ToggleSwitch label={t('task.template.autoCreate')} description={t('task.template.autoCreateHint')} checked={autoCreate} onChange={setAutoCreate} className="py-1" />
          <FormRow label={t('task.template.active')} labelWidth="sm" className="items-center py-1.5">
            <SegmentedControl
              aria-label={t('task.template.active')}
              size="sm"
              value={isActive ? 'active' : 'inactive'}
              onChange={(v) => setIsActive(v === 'active')}
              options={[
                { value: 'active', label: t('common.active') },
                { value: 'inactive', label: t('common.inactive') },
              ]}
            />
          </FormRow>
        </div>

        <div className="flex items-center gap-3 border-t border-border px-4 py-3">
          <Link to={`/task-templates/${id}`}>
            <Button type="button" variant="secondary">
              {t('common.cancel')}
            </Button>
          </Link>
          <span className="flex-1" />
          <Button type="button" onClick={handleSubmit} disabled={!canSubmit || isSubmitting} isLoading={isSubmitting}>
            {t('common.saveChanges')}
          </Button>
        </div>
      </div>
    </div>
  );
});

export default TaskTemplateEditPage;
